/**
 * 資源レジストリ。
 *
 * create() で獲得する資源（リスナ・オブザーバ・タイマ・worker 等）は必ずここを経由して
 * 獲得する。dispose() で一括解放し、残数を activeCounts() で観測できるようにすることで、
 * 「dispose 後にリスナ/worker 残留なし」（M2 verify）を機械的に検証可能にする。
 */

export type Disposer = () => void;

export type ResourceKind = 'listener' | 'observer' | 'timer' | 'worker' | 'other';

const KINDS: readonly ResourceKind[] = ['listener', 'observer', 'timer', 'worker', 'other'];

/** 種別ごとの生存数。 */
export type ResourceCounts = Record<ResourceKind, number>;

interface Entry {
  kind: ResourceKind;
  dispose: Disposer;
}

export class DisposeRegistry {
  private readonly entries = new Set<Entry>();
  private disposed = false;

  /** 任意の資源を登録する。戻り値を呼ぶと即時解放＆登録解除（二重解放は無害）。 */
  add(kind: ResourceKind, dispose: Disposer): Disposer {
    if (this.disposed) {
      dispose();
      return () => {};
    }
    const entry: Entry = { kind, dispose };
    this.entries.add(entry);
    return () => {
      if (!this.entries.delete(entry)) return;
      entry.dispose();
    };
  }

  /** addEventListener の登録つきラッパ。 */
  listen(
    target: EventTarget,
    type: string,
    handler: EventListenerOrEventListenerObject,
    options?: AddEventListenerOptions | boolean,
  ): Disposer {
    target.addEventListener(type, handler, options);
    return this.add('listener', () => target.removeEventListener(type, handler, options));
  }

  /** disconnect() を持つオブザーバ（Resize/Intersection/Mutation）の登録。 */
  observer<T extends { disconnect: () => void }>(instance: T): T {
    this.add('observer', () => instance.disconnect());
    return instance;
  }

  /** setTimeout の登録つきラッパ（発火時に自動で登録解除）。 */
  timeout(handler: () => void, ms: number): Disposer {
    let release: Disposer = () => {};
    const id = setTimeout(() => {
      release();
      handler();
    }, ms);
    release = this.add('timer', () => clearTimeout(id));
    return release;
  }

  /** setInterval の登録つきラッパ。 */
  interval(handler: () => void, ms: number): Disposer {
    const id = setInterval(handler, ms);
    return this.add('timer', () => clearInterval(id));
  }

  /** 種別ごとの生存数（dispose 監査フックの実体）。 */
  activeCounts(): ResourceCounts {
    const counts = Object.fromEntries(KINDS.map((k) => [k, 0])) as ResourceCounts;
    for (const entry of this.entries) counts[entry.kind] += 1;
    return counts;
  }

  /** 生存数の合計。 */
  activeTotal(): number {
    return this.entries.size;
  }

  /** 全資源を登録の逆順に解放する（依存の後入れ先出し）。 */
  disposeAll(): void {
    this.disposed = true;
    const entries = [...this.entries].reverse();
    this.entries.clear();
    for (const entry of entries) entry.dispose();
  }
}
